import z from 'zod';
import { sumDecimals } from '../utils/decimal.utils.js';
import { zodDecimal } from './decimal.types.js';
import { ECRITURE_LOCK_LABELS, } from './kept-ecritures.types.js';
const accountingAccountSelect = {
    id: true,
    code: true,
    name: true,
    supplierPayable: { select: { name: true } },
};
export const baseImmobilisation = {
    select: {
        id: true,
        name: true,
        libelle: true,
        dateAchat: true,
        dateStartService: true,
        supplierId: true,
        supplier: { select: { id: true, name: true, financialAccountId: true } },
        valeurAchat: true,
        valeurResiduelle: true,
        tva: true,
        amortizationYear: true,
        amortizationMethod: true,
        immobilisationAccountId: true,
        amortissementAccountId: true,
        dotationAuxAmortAccountId: true,
        ventilations: {
            select: {
                id: true,
                accountingCategoryId: true,
                percentage: true,
            },
        },
        immobilisationAccount: { select: accountingAccountSelect },
        amortissementAccount: { select: accountingAccountSelect },
        dotationAuxAmortAccount: { select: accountingAccountSelect },
    },
};
/** What each line of an immobilisation's écritures posts to: its acquisition, or a dotation. */
export const IMMOBILISATION_ACCOUNT_ROLES = [
    'immobilisation',
    'supplier',
    'dotation',
    'amortissement',
];
const lockedBy = (entry) => `${entry.reference} est verrouillée : ${ECRITURE_LOCK_LABELS[entry.lock]}`;
export const acquisitionLockedMessage = (entry) => `Les dates et montants d'acquisition ne sont plus modifiables, l'écriture ${lockedBy(entry)}. Le fournisseur et les comptes restent modifiables.`;
export const amortizationLockedMessage = (entry) => `Le plan d'amortissement n'est plus modifiable, la dotation ${lockedBy(entry)}.`;
export const BaseImmobilisationSchema = z.object({
    id: z.number().min(1, 'Le code court est requis'),
    libelle: z.string().min(1, 'Le libellé est requis'),
    dateAchat: z.date(),
    supplierId: z.number().int().positive('Le fournisseur est requis'),
    valeurAchat: z.number().positive("La valeur d'achat doit être positive"),
    valeurResiduelle: z.number().positive('La valeur résiduelle doit être positive').nullable(),
});
const tvaSchema = z.preprocess((val) => val === undefined ||
    val === null ||
    val === '' ||
    (typeof val === 'number' && Number.isNaN(val))
    ? 0
    : val, z
    .number()
    .min(0, 'Le taux de TVA doit être entre 0 et 100')
    .max(100, 'Le taux de TVA doit être entre 0 et 100'));
const serviceNotBeforePurchase = {
    check: (data) => data.dateStartService >= data.dateAchat,
    params: {
        message: "La date de mise en service ne peut pas précéder la date d'achat",
        path: ['dateStartService'],
    },
};
export const updateAcquisitionSchema = z
    .object({
    supplierId: z.number().int().positive('Le fournisseur est requis'),
    dateAchat: z.coerce.date(),
    dateStartService: z.coerce.date(),
    valeurAchat: z.number().positive("La valeur d'achat doit être positive"),
    tva: tvaSchema,
})
    .refine(serviceNotBeforePurchase.check, serviceNotBeforePurchase.params);
export const ventilationSchema = z.object({
    accountingCategoryId: z.number(),
    percentage: zodDecimal()
        .refine((value) => value.gte(0), 'Le pourcentage doit être entre 0 et 100')
        .refine((value) => value.lte(100), 'Le pourcentage doit être entre 0 et 100'),
});
export const createVentilationsSchema = z
    .array(ventilationSchema)
    .refine((arr) => {
    const valid = arr.filter((v) => v.accountingCategoryId > 0);
    return valid.length > 0;
}, {
    message: 'La ventilation analytique est obligatoire. Ajoutez au moins une ventilation.',
})
    .refine((arr) => {
    const valid = arr.filter((v) => v.accountingCategoryId > 0);
    return sumDecimals(valid.map((v) => v.percentage)).eq(100);
}, {
    message: 'La somme des pourcentages doit être égale à 100%.',
});
export const updateVentilationsSchema = z
    .object({
    ventilations: z.array(ventilationSchema),
})
    .refine((data) => {
    return sumDecimals(data.ventilations.map((v) => v.percentage)).eq(100);
}, {
    message: 'La somme des pourcentages doit être égale à 100%',
});
const MAX_AMORTIZATION_YEARS = 100;
const amortizationYearSchema = z.preprocess((val) => (val === undefined || val === null || val === '' ? 0 : val), z
    .number()
    .int()
    .positive('La durée doit être un nombre positif')
    .max(MAX_AMORTIZATION_YEARS, `La durée ne peut pas dépasser ${MAX_AMORTIZATION_YEARS} ans`));
const amortizationMethodSchema = z
    .union([z.undefined(), z.string()])
    .refine((val) => val === 'linear' || val === 'degressive', {
    message: "Le mode d'amortissement est requis",
});
export const updateAmortizationSchema = z.object({
    amortizationYear: amortizationYearSchema,
    amortizationMethod: amortizationMethodSchema,
});
const accountIdSchema = (message) => z.preprocess((val) => (val === undefined || val === null || val === '' ? 0 : val), z.number().int().positive(message));
export const updateAccountsSchema = z.object({
    immobilisationAccountId: accountIdSchema('Le compte immobilisation est requis'),
    amortissementAccountId: accountIdSchema('Le compte amortissement est requis'),
    dotationAuxAmortAccountId: accountIdSchema('Le compte dotation aux amortissements est requis'),
});
export const createImmobilisationSchema = z
    .object({
    name: z.string().min(1, 'Le code court est requis'),
    libelle: z.string().min(1, 'Le libellé est requis'),
    supplierId: z.number().int().positive('Le fournisseur est requis'),
    dateAchat: z.coerce.date(),
    dateStartService: z.coerce.date(),
    valeurAchat: z.number().positive("La valeur d'achat doit être positive"),
    tva: tvaSchema,
    immobilisationAccountId: accountIdSchema('Le compte immobilisation est requis'),
    amortissementAccountId: accountIdSchema('Le compte amortissement est requis'),
    dotationAuxAmortAccountId: accountIdSchema('Le compte dotation aux amort. est requis'),
    amortizationYear: amortizationYearSchema,
    amortizationMethod: amortizationMethodSchema,
    ventilations: createVentilationsSchema,
})
    .refine(serviceNotBeforePurchase.check, serviceNotBeforePurchase.params);
